import { z } from 'zod';

/**
 * Query string of `/embed/player`, the page other sites put in an <iframe>.
 * One parser so the route, the Share → Embed snippet builder and the host
 * script docs agree on names and defaults.
 *
 *   tones   comma-separated tone ids, order preserved, duplicates dropped.
 *   layout  compact | full. Defaults to full.
 *   live    "0" hides the "Plug in and play" tab (hosts without microphone
 *           permission). Anything else, or absent, shows it.
 *   theme   dark | light. Defaults to dark. The page itself is transparent,
 *           so the host's background shows around the player's corners.
 *   ref     free-form attribution tag a host chooses, echoed in analytics.
 */

export const MAX_EMBED_TONES = 10;

export const EMBED_LAYOUTS = ['full', 'compact'] as const;
export type EmbedLayout = (typeof EMBED_LAYOUTS)[number];
export const DEFAULT_EMBED_LAYOUT: EmbedLayout = 'full';

export const EMBED_THEMES = ['dark', 'light'] as const;
export type EmbedTheme = (typeof EMBED_THEMES)[number];
export const DEFAULT_EMBED_THEME: EmbedTheme = 'dark';

const idList = z
  .string()
  .transform((raw) =>
    raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map(Number)
      .filter((n) => Number.isSafeInteger(n) && n > 0)
  )
  .transform((ids) => Array.from(new Set(ids)).slice(0, MAX_EMBED_TONES));

const schema = z.object({
  tones: idList.optional().default(''),
  layout: z.enum(EMBED_LAYOUTS).optional(),
  live: z.string().optional(),
  theme: z.enum(EMBED_THEMES).optional(),
  ref: z
    .string()
    .trim()
    .max(64)
    .regex(/^[\w.-]*$/)
    .optional(),
});

export interface EmbedParams {
  toneIds: number[];
  layout: EmbedLayout;
  live: boolean;
  theme: EmbedTheme;
  ref: string | null;
}

type SearchParams = Record<string, string | string[] | undefined>;

const first = (v: string | string[] | undefined) => (Array.isArray(v) ? v[0] : v);

/** Never throws: anything malformed falls back to the default for that key. */
export function parseEmbedParams(searchParams: SearchParams): EmbedParams {
  const input = {
    tones: first(searchParams.tones),
    layout: first(searchParams.layout),
    live: first(searchParams.live),
    theme: first(searchParams.theme),
    ref: first(searchParams.ref),
  };
  // Parse key-by-key so one bad value (e.g. `layout=huge`) doesn't drop the
  // tone ids with it.
  const tones = schema.shape.tones.safeParse(input.tones);
  const layout = schema.shape.layout.safeParse(input.layout);
  const theme = schema.shape.theme.safeParse(input.theme);
  const ref = schema.shape.ref.safeParse(input.ref);

  const toneIds = tones.success ? tones.data : [];
  return {
    toneIds,
    layout: layout.success && layout.data ? layout.data : DEFAULT_EMBED_LAYOUT,
    live: input.live !== '0',
    theme: theme.success && theme.data ? theme.data : DEFAULT_EMBED_THEME,
    ref: ref.success && ref.data ? ref.data : null,
  };
}

/** Builds the `/embed/player` query for a snippet. Omits values at their default. */
export function buildEmbedQuery(opts: {
  toneIds: number[];
  layout?: EmbedLayout;
  live?: boolean;
  theme?: EmbedTheme;
  ref?: string;
}): string {
  const params = new URLSearchParams();
  params.set('tones', opts.toneIds.slice(0, MAX_EMBED_TONES).join(','));
  if (opts.layout && opts.layout !== DEFAULT_EMBED_LAYOUT) params.set('layout', opts.layout);
  if (opts.live === false) params.set('live', '0');
  if (opts.theme && opts.theme !== DEFAULT_EMBED_THEME) params.set('theme', opts.theme);
  if (opts.ref) params.set('ref', opts.ref);
  return params.toString();
}
